import { useState, useCallback } from 'react'

/**
 * Return type for dropdown state hook
 */
export interface UseDropdownStateReturn {
  /** Whether the dropdown is open */
  isOpen: boolean
  /** Open the dropdown */
  open: () => void
  /** Close the dropdown */
  close: () => void
  /** Toggle the dropdown open/closed */
  toggle: () => void
  /** Set the dropdown state directly */
  setIsOpen: (isOpen: boolean) => void
}

/**
 * Hook that manages dropdown visibility state
 * Provides convenient methods for opening, closing, and toggling
 *
 * @param initialState - Initial open state (default: false)
 * @returns Dropdown state and control methods
 */
export const useDropdownState = (
  initialState: boolean = false
): UseDropdownStateReturn => {
  const [isOpen, setIsOpen] = useState(initialState)

  const open = useCallback(() => {
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  return {
    isOpen,
    open,
    close,
    toggle,
    setIsOpen,
  }
}
